import { provideRtkDesignSystem } from '@cloudflare/realtimekit-ui';
import {
  applyJoinButtonBrandRampToHost,
  hexToBrandRgbString,
} from './applyRtkBrandTokensForFocusRing';
import { collectElementsDeepShadow } from './applyScopedControlbarHoverBackground';
import { ensureRtkMeetingTextContrast } from './ensureRtkMeetingTextContrast';
import { collectSetupScreenDesignSystemTargets } from './meetingTheme';
import {
  mixHex,
  normalizeThemeHex,
  pickAccessibleForeground,
  relativeLuminance,
} from './wcagContrast';

/** Nudge Join fill toward `buttonColor` when it reads as an accent (not white/black). */
const SETUP_JOIN_ACCENT_TINT = 0.07;

/**
 * Resolved fill for setup Join (`rtk-button` primary): `buttonBackground`, optionally mixed toward
 * `buttonColor` so the control matches brand accent without replacing API defaults wholesale.
 *
 * @param {object | null | undefined} themeDetails
 * @returns {string | null} normalized `#rrggbb`
 */
function resolveSetupJoinFillHex(themeDetails) {
  const preset = themeDetails?.presetTheme ?? {};
  const bg = normalizeThemeHex(
    themeDetails?.buttonBackground ?? preset.buttonBackground
  );
  const accent = normalizeThemeHex(themeDetails?.buttonColor ?? preset.buttonColor);
  if (!bg) return null;
  if (!accent || accent === bg) return bg;

  const La = relativeLuminance(accent);
  if (La != null && (La > 0.97 || La < 0.03)) return bg;

  return mixHex(bg, accent, SETUP_JOIN_ACCENT_TINT) ?? bg;
}

/**
 * Header-aligned ramp for chat bubbles/menus (`rtk-chat`, `rtk-message-view`).
 */
const HEADER_BRAND_SHADES = ['300', '400', '500', '600', '700', '800'];

const TEXT_ON_BRAND_MIN_RATIO = 4.5;

/**
 * Skip header colors that are white/light **text** tokens but unusable as bubble fill (e.g. Islamic
 * Pink `headerColor: #fff`). Dark/accent header colors (Garden Peach, etc.) stay first-priority.
 */
const CHAT_BRAND_FILL_MAX_LUMINANCE = 0.93;

/**
 * Primary `rtk-button` (Join) uses `--rtk-colors-text-on-brand-*`. Ensure `buttonColor` on
 * `buttonBackground` meets WCAG; if not, pick black/white or adjusted foreground.
 *
 * @param {HTMLElement | null} setupEl `<rtk-setup-screen>`
 * @param {object | null | undefined} themeDetails from `getMeetingThemeDetails`
 */
export function ensureSetupPrimaryButtonTextOnBrand(setupEl, themeDetails) {
  if (!setupEl || typeof window === 'undefined') return;

  const bgHex = resolveSetupJoinFillHex(themeDetails);
  if (!bgHex) return;

  const preferred =
    normalizeThemeHex(themeDetails?.buttonColor ?? themeDetails?.presetTheme?.buttonColor) ??
    null;

  const resolved = pickAccessibleForeground(bgHex, preferred, {
    minRatio: TEXT_ON_BRAND_MIN_RATIO,
  });
  if (!resolved) return;

  provideRtkDesignSystem(setupEl, {
    colors: { 'text-on-brand': resolved },
  });
}

/**
 * Apply header-derived brand RGB to any RTK host (`rtk-chat`, `rtk-message-view`, etc.).
 *
 * @param {HTMLElement | null} hostEl
 * @param {object | null | undefined} themeDetails from `getMeetingThemeDetails`
 */
export function applyHeaderBrandRampToHost(hostEl, themeDetails) {
  if (!hostEl || typeof document === 'undefined') return;

  const hex = resolveChatBubbleBrandFillHex(themeDetails);
  if (!hex) return;

  const rgb = hexToBrandRgbString(hex);
  if (!rgb) return;

  for (const shade of HEADER_BRAND_SHADES) {
    hostEl.style.setProperty(`--rtk-colors-brand-${shade}`, rgb);
  }
}

/**
 * Bubble brand fill: prefer accent-like colors; skip near-white `headerColor` (sidebar title text).
 *
 * @param {object | null | undefined} themeDetails
 * @returns {string | null} normalized `#rrggbb`
 */
export function resolveChatBubbleBrandFillHex(themeDetails) {
  const preset = themeDetails?.presetTheme ?? {};
  const candidates = [
    themeDetails?.headerColor ?? preset.headerColor,
    themeDetails?.headerBackground ?? preset.headerBackground,
    themeDetails?.buttonColor ?? preset.buttonColor,
    themeDetails?.buttonBackground ?? preset.buttonBackground,
  ];

  for (const raw of candidates) {
    const hex = normalizeThemeHex(raw);
    if (!hex) continue;
    const L = relativeLuminance(hex);
    if (L != null && L < CHAT_BRAND_FILL_MAX_LUMINANCE) {
      return hex;
    }
  }

  for (const raw of candidates) {
    const hex = normalizeThemeHex(raw);
    if (hex) return hex;
  }
  return null;
}

/**
 * Same as {@link resolveChatBubbleBrandFillHex} — text-on-brand vs bubble fill.
 *
 * @param {object | null | undefined} themeDetails
 * @returns {string | null} normalized `#rrggbb`
 */
export function resolveChatBrandFillHex(themeDetails) {
  return resolveChatBubbleBrandFillHex(themeDetails);
}

/**
 * Self-sent bubbles use `--rtk-colors-brand-300` as fill (see `rtk-chat-message.css`) and
 * `text-on-brand-*` for label/body. {@link ensureRtkMeetingTextContrast} sets `text-on-brand` equal to
 * `text` for the **sidebar/peach** surface; when header/teal === brand fill, that makes bubble text
 * the same color as the bubble (Garden Peach, etc.). Override `text-on-brand` vs **brand** fill.
 *
 * @param {HTMLElement | null} hostEl `<rtk-chat>`, `rtk-message-view`, `rtk-chat-message`, …
 * @param {object | null | undefined} themeDetails from `getMeetingThemeDetails`
 */
export function ensureChatBubbleTextOnBrand(hostEl, themeDetails) {
  if (!hostEl || typeof window === 'undefined') return;

  const brandHex = resolveChatBrandFillHex(themeDetails);
  if (!brandHex) return;

  const preferred =
    normalizeThemeHex(themeDetails?.buttonColor ?? themeDetails?.presetTheme?.buttonColor) ??
    '#ffffff';

  const resolved =
    pickAccessibleForeground(brandHex, preferred, {
      minRatio: TEXT_ON_BRAND_MIN_RATIO,
    }) ?? preferred;

  provideRtkDesignSystem(hostEl, {
    colors: { 'text-on-brand': resolved },
  });
}

/**
 * Chat (`rtk-message-view`, menus) inherits brand from `<rtk-meeting>` (`buttonBackground`).
 * Override on `<rtk-chat>` with header palette where applicable.
 *
 * @param {HTMLElement | null} rtkChatEl
 * @param {object | null | undefined} themeDetails from `getMeetingThemeDetails`
 */
export function applyChatPanelBrandOverride(rtkChatEl, themeDetails) {
  applyHeaderBrandRampToHost(rtkChatEl, themeDetails);
  ensureChatBubbleTextOnBrand(rtkChatEl, themeDetails);
}

/** Hosts that render self-message bubbles (kit uses `rtk-message-view`; older builds use `rtk-chat-message`). */
const CHAT_BUBBLE_MESSAGE_TAGS = ['rtk-message-view', 'rtk-chat-message'];

/**
 * Set brand ramp + readable `text-on-brand` on every message row host under `<rtk-meeting>`.
 *
 * @param {HTMLElement | null} meetingHostEl outer `<rtk-meeting>`
 * @param {object | null | undefined} themeDetails from `getMeetingThemeDetails`
 */
/**
 * Unread / selector chrome (`rtk-chat-selector-ui`) uses `brand-500` + `text-on-brand-*` for badges.
 * Nested mounts may not inherit `rtk-chat` tokens in time — patch hosts explicitly.
 *
 * @param {HTMLElement | null} meetingHostEl outer `<rtk-meeting>`
 * @param {object | null | undefined} themeDetails
 */
export function applyChatSelectorUiTokens(meetingHostEl, themeDetails) {
  if (!meetingHostEl?.shadowRoot) return;

  const hosts = collectElementsDeepShadow(meetingHostEl.shadowRoot, 'rtk-chat-selector-ui');
  for (const el of hosts) {
    applyHeaderBrandRampToHost(el, themeDetails);
    ensureChatBubbleTextOnBrand(el, themeDetails);
  }
}

export function applyMessageViewBrandOverrides(meetingHostEl, themeDetails) {
  if (!meetingHostEl?.shadowRoot) return;

  const preset = themeDetails?.presetTheme ?? {};
  const headerColor = themeDetails?.headerColor ?? preset.headerColor;
  const headerBackground =
    themeDetails?.headerBackground ?? preset.headerBackground;

  for (const tag of CHAT_BUBBLE_MESSAGE_TAGS) {
    const views = collectElementsDeepShadow(meetingHostEl.shadowRoot, tag);
    for (const el of views) {
      ensureRtkMeetingTextContrast(el, {
        preferredTextHex: headerColor,
        headerBackground,
        surfaceBackgroundHex: headerBackground,
      });
      applyHeaderBrandRampToHost(el, themeDetails);
      ensureChatBubbleTextOnBrand(el, themeDetails);
    }
  }
}

/**
 * Join / pre-join UI: derive fill from `buttonBackground`, tint lightly toward `buttonColor` when it
 * reads as an accent (skips near-white/black labels), set brand `600`/`700` darker than `500` for
 * hover/active, and WCAG-safe `text-on-brand` vs that resolved fill.
 *
 * @param {HTMLElement | null} meetingHostEl outer `<rtk-meeting>`
 * @param {object | null | undefined} themeDetails from `getMeetingThemeDetails`
 */
export function applySetupScreenJoinButtonTheme(meetingHostEl, themeDetails) {
  const setups = collectSetupScreenDesignSystemTargets(meetingHostEl);

  for (const el of setups) {
    const fillHex = resolveSetupJoinFillHex(themeDetails);
    if (!fillHex) continue;
    applyJoinButtonBrandRampToHost(el, fillHex);
    ensureSetupPrimaryButtonTextOnBrand(el, themeDetails);
  }
}
